import React, { useEffect, useState } from 'react';
import { getMainAppOrigin } from '../../../config/adminHost';
import { useNotification } from '../../../../context/NotificationContext';
import { getComingSoonMode, setComingSoonMode } from './comingSoonSettingsApi';
import { generateSafeAccessCode } from './generateComingSoonAccessCode';

const ComingSoonSettingsCard = () => {
  const { showToast } = useNotification();
  const [enabled, setEnabled] = useState(false);
  const [hasAccessCode, setHasAccessCode] = useState(false);
  const [codeDraft, setCodeDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [codeVisible, setCodeVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getComingSoonMode();
        if (cancelled) return;
        setEnabled(Boolean(data.enabled));
        setHasAccessCode(Boolean(data.hasAccessCode));
      } catch (error) {
        console.error('Error loading coming soon settings:', error);
      }
    };
    load();
    const interval = setInterval(load, 12000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const save = async ({ nextEnabled, accessCode }) => {
    setSaving(true);
    try {
      const result = await setComingSoonMode({
        enabled: nextEnabled,
        accessCode,
      });
      setEnabled(Boolean(result.enabled));
      setHasAccessCode(Boolean(result.hasAccessCode));
      if (accessCode) {
        setCodeDraft(accessCode);
        setCodeVisible(true);
      }
      showToast('Coming soon settings updated.', 'success');
    } catch (err) {
      showToast(err?.message || 'Failed to update coming soon settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateCode = () => {
    setCodeDraft(generateSafeAccessCode());
    setCodeVisible(true);
  };

  const handleCopyCode = async () => {
    if (!codeDraft.trim()) return;
    try {
      await navigator.clipboard.writeText(codeDraft.trim());
      showToast('Access code copied.', 'success');
    } catch {
      showToast('Could not copy the code.', 'error');
    }
  };

  return (
    <section className="admin-card app-settings-card">
      <div className="app-settings-row">
        <div className="app-settings-row__text">
          <h3 className="app-settings-row__title">Coming soon page</h3>
          <p className="app-settings-row__desc">
            Show a public splash page with the Neat Schedule logo until launch. People with the access
            code can still use the site. The admin panel is never blocked.
          </p>
        </div>
        <div className="app-settings-row__action">
          <label className="pkg-dialog-check" htmlFor="coming-soon-mode">
            <input
              id="coming-soon-mode"
              type="checkbox"
              checked={enabled}
              disabled={saving}
              onChange={() => save({ nextEnabled: !enabled })}
            />
            Enabled
          </label>
        </div>
      </div>

      <div className="app-settings-row app-settings-row--stacked">
        <p className="app-settings-help">
          {hasAccessCode
            ? 'An access code is already saved. Enter a new one only if you want to replace it.'
            : 'Generate a code, copy it for testers, then save it before turning this on.'}
        </p>
        <div className="platform-credentials-form">
          <div className="pkg-dialog-field">
            <label htmlFor="coming-soon-access-code">Access code</label>
            <input
              id="coming-soon-access-code"
              className="pkg-dialog-input"
              type={codeVisible ? 'text' : 'password'}
              value={codeDraft}
              onChange={(e) => setCodeDraft(e.target.value.toUpperCase())}
              placeholder={hasAccessCode ? '••••••••' : 'Generate or type a launch access code'}
              autoComplete="off"
              spellCheck="false"
            />
          </div>
          <div className="platform-credentials-actions">
            <button
              type="button"
              className="pkg-btn pkg-btn-secondary"
              disabled={saving}
              onClick={handleGenerateCode}
            >
              Generate code
            </button>
            <button
              type="button"
              className="pkg-btn pkg-btn-secondary"
              disabled={saving || !codeDraft.trim()}
              onClick={handleCopyCode}
            >
              Copy
            </button>
            <button
              type="button"
              className="pkg-btn pkg-btn-primary"
              disabled={saving || !codeDraft.trim()}
              onClick={() => save({ nextEnabled: enabled, accessCode: codeDraft })}
            >
              {saving ? 'Saving…' : 'Save code'}
            </button>
            <a
              className="pkg-btn"
              href={`${getMainAppOrigin()}/coming-soon-preview`}
              target="_blank"
              rel="noreferrer"
            >
              Open preview
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComingSoonSettingsCard;
